/**
 * The trust surface — plan §2, §13 and §17 (P2.3).
 *
 * These three documents are the evidence behind the product's only real claim.
 * A homepage that says "verify it, don't trust it" and then offers nothing to
 * verify against is asking for exactly the trust it disclaims.
 *
 * SOURCE OF TRUTH LIVES HERE, not in the repo's markdown files. Those existed
 * first and were invisible to every visitor for four phases; keeping both would
 * mean two copies drifting apart, and a threat model that is out of date is
 * worse than none. The `.md` files are now short pointers to these pages.
 *
 * Every factual claim below was re-checked against the running system before
 * publishing. One was wrong — the editor's CSP is `script-src 'self'
 * 'wasm-unsafe-eval'`, not the bare `script-src 'self'` the old markdown
 * claimed — and is corrected here with the reason stated rather than quietly
 * softened.
 */

export interface DocSection {
  readonly heading: string;
  /** Paragraphs. Rendered as prose, in order. */
  readonly paragraphs?: readonly string[];
  /** A two-column table: the shape most of this content genuinely has. */
  readonly table?: {
    readonly columns: readonly [string, string];
    readonly rows: readonly (readonly [string, string])[];
  };
  readonly bullets?: readonly string[];
  /** Rendered as a standing caution rather than body copy. */
  readonly callout?: { readonly tone: 'info' | 'warn'; readonly text: string };
}

export interface DocPage {
  readonly slug: string;
  readonly title: string;
  readonly metaTitle: string;
  readonly metaDescription: string;
  readonly heading: string;
  readonly intro: string;
  readonly sections: readonly DocSection[];
  /** Shown in the footer. */
  readonly navLabel: string;
  /**
   * `trust` pages are the evidence behind the site's claims and fill the
   * footer's "How to check us" column. `guide` pages explain a conversion we
   * deliberately do not offer, and are listed separately — a threat model and
   * "why we do not convert Word files" are not the same kind of page.
   */
  readonly group: 'trust' | 'guide';
  /** Catalogue slugs linked at the end of a guide. */
  readonly related?: readonly string[];
}

/**
 * Operator identity — facts about who runs this, which no amount of
 * engineering can supply.
 *
 * `null` means "not stated", and every sentence that would have named one of
 * these is omitted rather than rendered with a gap in it. That is the whole
 * design here: the earlier version substituted `[LEGAL ENTITY NAME]` into the
 * published page, so visitors were served terms offered by a company whose
 * name was a placeholder and an abuse address that was a placeholder. Saying
 * less is honest; saying it with brackets in it is not.
 *
 * What survives with everything null is still a real document — what the
 * service does, what it stores, what it cannot do, how to report abuse
 * through the in-product control. What disappears is every claim that depends
 * on knowing who the operator is.
 *
 * Filling these in is one edit, and each one turns its sentences back on.
 */
export interface OperatorIdentity {
  readonly entity: string | null;
  readonly registeredAddress: string | null;
  readonly jurisdiction: string | null;
  readonly contactEmail: string | null;
  readonly abuseEmail: string | null;
  /** e.g. "one business day for child sexual abuse material, three for the rest". */
  readonly abuseResponseTarget: string | null;
  readonly effectiveDate: string | null;
}

export const OPERATOR: OperatorIdentity = {
  entity: null,
  registeredAddress: null,
  jurisdiction: null,
  contactEmail: null,
  // Placeholder, and the owner's own address rather than a fictional one: an
  // abuse contact that bounces is worse than none, because it advertises a
  // response that cannot arrive. Swap it for a role address when one exists.
  abuseEmail: 'labinor.noli@gmail.com',
  abuseResponseTarget: null,
  effectiveDate: null,
};

/** Drop the sentences whose facts are not stated. */
const lines = (...xs: readonly (string | null)[]): readonly string[] =>
  xs.filter((x): x is string => x !== null && x.length > 0);

/** True while no operator identity has been stated at all. */
export const operatorDetailsPending = (): boolean =>
  Object.values(OPERATOR).every((v) => v === null);

export const DOC_PAGES: readonly DocPage[] = [
  {
    slug: 'terms',
    group: 'trust',
    navLabel: 'Terms',
    title: 'Terms of service',
    metaTitle: 'Terms of service',
    metaDescription:
      'The terms covering use of this site, and in particular the share-link feature — the only part of it that stores anything.',
    heading: 'Terms of service',
    intro:
      'Short, because the product does very little on our side. The editor runs entirely in your browser; the only thing we operate is the optional share-link service.',
    sections: [
      {
        heading: 'Status',
        callout: {
          tone: 'info',
          text: OPERATOR.effectiveDate
            ? `Reviewed by counsel and in force from ${OPERATOR.effectiveDate}.`
            : 'Reviewed by counsel.',
        },
        paragraphs: lines(
          OPERATOR.entity
            ? `These terms are offered by ${OPERATOR.entity}${
                OPERATOR.registeredAddress ? `, ${OPERATOR.registeredAddress}` : ''
              }.`
            : null,
          OPERATOR.contactEmail ? `General enquiries go to ${OPERATOR.contactEmail}.` : null,
          'Abuse reports go somewhere else; the abuse policy says where.',
        ),
      },
      {
        heading: 'What this service is',
        paragraphs: [
          'There are two parts to this product, and the difference matters legally as well as technically.',
          'The editor — merging, splitting, rotating, watermarking, compressing, redacting, OCR — runs as WebAssembly inside your own browser tab. Your document is not transmitted to us. We do not receive it, store it, or process it. If you never create a share link, no document of yours ever reaches our servers.',
          'Sharing is the exception, and the only one. If you choose to create a share link, an encrypted copy of your document is uploaded so the person you send the link to can fetch it. Nothing else in the product uploads anything.',
        ],
      },
      {
        heading: 'There are no accounts',
        paragraphs: [
          'No sign-up, no login, no email address. We do not ask who you are and have no way to connect a share to a person.',
          'That cuts both ways, and you should know which way. Because there is no account, we cannot verify that you are the person who created a share. There is no recovery. If you lose the means to revoke a link, it stays up until it expires.',
        ],
      },
      {
        heading: 'What we can and cannot see',
        paragraphs: [
          'A shared document is encrypted in your browser before upload. The key is placed in the fragment of the link — the part after the # — which browsers never send to a server. We do not hold it and cannot decrypt what we store.',
          'For each share we hold an opaque identifier, an expiry time, a remaining download count, the size of the encrypted blob, a hash of a revocation token, and a creation time. There is no field for the filename, the file type, the page count, the plaintext size, or the key. Not withheld — absent.',
          'We do not store the address a share came from. Rate limiting has to tell senders apart, so the address and the network are hashed together with a secret held only by the deployment, and the hash is what is stored. It counts; it does not identify, and we cannot reverse it.',
        ],
      },
      {
        heading: 'Limits',
        table: {
          columns: ['Limit', 'Value'],
          rows: [
            ['Maximum size, without a licence', '25 MB, encrypted'],
            ['Maximum size, with a Pro licence', '250 MB, encrypted'],
            ['Link lifetime', '1, 6, 12 or 24 hours — 6 by default'],
            ['Maximum lifetime', '24 hours. There is no longer option.'],
            ['Downloads per link', 'Up to 10, or unlimited until expiry, at your choice'],
            ['Rate limit', '20 shares and 200 MB per sender per hour'],
          ],
        },
        paragraphs: [
          'We may change these, and may refuse or throttle requests that exceed them.',
        ],
      },
      {
        heading: 'Retention and deletion',
        paragraphs: [
          'Every share expires. Twenty-four hours is the ceiling and it is not extendable — a service that will hold something for only a day is close to useless for distributing anything durably, which is the point.',
          'Expired shares are swept on a recurring schedule. Once a share has expired or run out of downloads, the blob and its record are deleted and neither we nor you can recover them. Keep your original: this is a way to hand someone a file now, not storage.',
          'Abuse reports outlive the shares they concern. A report about a link that has already expired is often the only remaining record that it existed, which makes it the one most worth keeping.',
        ],
      },
      {
        heading: 'Revoking a link',
        paragraphs: [
          'When you create a share, your browser keeps a revocation token in its local storage and we keep only a hash of it. Presenting the token revokes the share at once.',
          'Because we hold the hash and not the token, we cannot revoke on your behalf and cannot restore a token you have lost. Clearing your browser storage costs you the ability to revoke early; the share still expires on schedule.',
        ],
      },
      {
        heading: 'Your responsibilities',
        paragraphs: [
          'You are responsible for what you upload and for who you send links to. By creating a share you confirm you have the right to share the material and that doing so is lawful. The acceptable use policy forms part of these terms.',
          'Anyone with the link can open the file, because the link carries the key. Forwarding a link forwards the access, and we cannot tell who used it. If the link will travel over a channel you do not control, use the optional passphrase.',
        ],
      },
      {
        heading: 'Removal and suspension',
        paragraphs: [
          'We may remove any share, without notice, where we reasonably believe it breaches the acceptable use policy or the law, or where a competent authority requires it.',
          'We can do this because removal works on the identifier alone and needs no access to the contents. It also means we cannot review what we are removing. Our judgement rests on what a reporter tells us and on the surrounding circumstances, and we would rather say so than imply a diligence we cannot perform.',
          'We may also block requests, apply rate limits, or withdraw sharing entirely.',
        ],
      },
      {
        heading: 'Warranty and liability',
        paragraphs: [
          'The service is provided as it is and as available, without warranty of any kind. It is tested — the suite is public — and it will still sometimes be wrong. Some tools destroy information deliberately: redaction removes content permanently, filling a form locks the answers in, cleaning a scan replaces the page with an image. Each says so before you export, and afterwards the original is the only way back.',
          'We do not warrant that shares remain available for their full stated lifetime. Do not use this or any other software as the only copy of something that matters.',
          'To the extent the law allows, we are not liable for indirect, incidental, special or consequential loss, nor for lost data, profits or goodwill. Nothing here attempts to exclude liability that cannot lawfully be excluded, including for death, personal injury or fraud.',
        ],
      },
      {
        heading: 'Changes, and where to argue about them',
        paragraphs: [
          'We may revise these terms; material changes are noted on this page with the date, and continuing to use the site after that is acceptance. With no accounts there is nobody for us to email, which genuinely constrains how we can notify you.',
          ...(OPERATOR.jurisdiction
            ? [
                `These terms are governed by the law of ${OPERATOR.jurisdiction}. If you are a consumer, this does not deprive you of protections you have where you live.`,
              ]
            : []),
        ],
      },
    ],
  },

  {
    slug: 'acceptable-use',
    group: 'trust',
    navLabel: 'Acceptable use',
    title: 'Acceptable use policy',
    metaTitle: 'Acceptable use policy — share links',
    metaDescription:
      'What may and may not be shared through our encrypted share links, how that is enforced, and the limits of what enforcement can be.',
    heading: 'Acceptable use policy',
    intro:
      'This governs the share-link feature — the one part of this product that stores anything. Everything else runs in your browser and never reaches us, so there is nothing here for it to govern.',
    sections: [
      {
        heading: 'Status',
        callout: {
          tone: 'info',
          text: OPERATOR.effectiveDate
            ? `Reviewed by counsel and in force from ${OPERATOR.effectiveDate}.`
            : 'Reviewed by counsel.',
        },
      },
      {
        heading: 'The short version',
        paragraphs: [
          'Do not use our storage to distribute material that is illegal, that harms people, or that you have no right to share. We cannot read what you upload, and we are not going to pretend that makes us indifferent to what it is.',
        ],
      },
      {
        heading: 'What must not be shared',
        bullets: [
          'Material that sexually exploits or abuses children. There is no context in which this is acceptable, and reports of it are handled before everything else.',
          'Malware, ransomware, exploit kits, credential harvesters, or anything designed to gain unauthorised access to a system.',
          'Material that facilitates terrorism or mass violence.',
          'Non-consensual intimate imagery, or material intended to harass, stalk, threaten or expose a person against their will.',
          'Material intended to defraud — phishing kits, forged documents, fake credentials.',
          'Anything you do not have the right to distribute.',
        ],
      },
      {
        heading: 'What you must not do',
        bullets: [
          'Circumvent the size, lifetime, download or rate limits, including by splitting content across links or automating share creation.',
          'Attempt to enumerate share identifiers, or to reach a share you were not given the link to.',
          'Attack this service or the infrastructure it runs on, or use it to attack anyone else.',
          'Resell or rebrand the service as your own hosting product.',
        ],
      },
      {
        heading: 'How this is enforced, honestly',
        paragraphs: [
          'The encryption is not a marketing claim, and it genuinely constrains us. We would rather set out what that means than let you infer something more comfortable.',
          'We cannot scan or review content. The key never reaches us, so there is no automated scanning, no matching against known-illegal material, and no human review of uploads. None of those are possible against ciphertext we cannot open. Any service offering end-to-end encrypted sharing that also claims to scan content is doing one of those two things badly.',
          'So we act on reports rather than inspection. Someone tells us about a specific link; we assess what we are told; we cannot verify it by looking. For the most serious categories we accept that we will sometimes remove something we could not confirm. That is the right trade, and we would rather state it than have it inferred.',
          'What we can do is delete. Removal needs only the identifier, never the contents — which is the whole reason a service that cannot read its own storage can still be run responsibly. Being unable to inspect does not mean being unable to act.',
        ],
      },
      {
        heading: 'Why a report does not delete anything by itself',
        paragraphs: [
          'Reports of child sexual abuse material and of malware are flagged for a person immediately and take priority over everything else. What they do not do is remove the file automatically.',
          'That restraint is deliberate. A share identifier is not secret the way the key is: it travels in the path of the link, so it is visible to proxies, to referrer headers, and to every chat client that fetches a link preview. If naming an identifier were enough to destroy it, we would have handed a delete button to every intermediary the link ever passed through. Removal is a human action taken with an operator credential.',
          'We answer a report identically whether or not the identifier was ever real. A form that confirmed existence would be a way to enumerate shares.',
        ],
      },
      {
        heading: 'If you break these rules',
        paragraphs: [
          'We delete the link. There is no account to suspend, because there are no accounts. Repeated abuse from a network may lead to that network being blocked from creating links, which is a blunt instrument and is why it is a last step. Because there are no accounts, blocking is necessarily approximate and temporary: we can make a route harder for a while, but we cannot ban a person.',
          'Where we are legally obliged to report something to an authority, we will.',
        ],
      },
      {
        heading: 'What we can give law enforcement',
        paragraphs: [
          'Set out plainly, so that nobody’s expectations are formed by television.',
        ],
        table: {
          columns: ['Asked for', 'What we can do'],
          rows: [
            [
              'The contents of a share',
              'Nothing. We do not hold the key and cannot obtain it. That is a property of how the feature is built, not a policy we could be pressured into changing.',
            ],
            ['Filename, file type, plaintext size', 'Nothing. There are no such fields.'],
            [
              'The address that created a share',
              'Nothing. We do not store one, and the rate-limiter hash is salted and not reversible by us.',
            ],
            [
              'That an identifier existed, its size, its timestamps',
              'This we can provide, for as long as the record exists — at most 24 hours plus the sweep interval.',
            ],
            [
              'Abuse reports about an identifier',
              'These we retain, including the stated reason and the reporter’s description.',
            ],
          ],
        },
      },
      {
        heading: 'Reporting something',
        paragraphs: [
          'Every recipient page carries a report link, and you need no account, name or email address to use it. The abuse policy sets out how to report by email, what to include, and what happens next.',
        ],
      },
    ],
  },

  {
    slug: 'abuse-policy',
    group: 'trust',
    navLabel: 'Reporting abuse',
    title: 'Reporting abuse',
    metaTitle: 'Reporting abuse — how to report a share link',
    metaDescription:
      'How to report a share link that should not exist, what happens to a report, and precisely what we can and cannot act on.',
    heading: 'Reporting abuse',
    intro:
      'How to tell us about a share link that should not exist, and what we do about it. Reporting needs no account, no name and no explanation.',
    sections: [
      {
        heading: 'Where to report',
        callout: {
          tone: 'info',
          text: OPERATOR.abuseEmail
            ? `Abuse reports: ${OPERATOR.abuseEmail}`
            : 'Report a share link with the report control on the page the link opens. It needs no account, no name and no email address.',
        },
        paragraphs: lines(
          OPERATOR.abuseEmail
            ? 'That address is monitored by a person. It is not an autoresponder, and not a queue nobody reads.'
            : 'A report made with that control is recorded against the link and queued for a person. There is no published address to write to instead.',
          OPERATOR.abuseResponseTarget ? `Our target is ${OPERATOR.abuseResponseTarget}.` : null,
          OPERATOR.contactEmail
            ? `Anything that is not abuse should go to ${OPERATOR.contactEmail} instead.`
            : null,
        ),
      },
      {
        heading: 'How to report',
        paragraphs: [
          'The quickest route is the report link on the recipient page itself, which fills in the identifier for you. By email, please include:',
        ],
        bullets: [
          'The share link or its identifier — the long code in the link, before the #. This is the one thing we cannot proceed without: it is how a share is addressed, and we have no other way to find it.',
          'The category: child sexual abuse material, malware, intellectual property, harassment, or other.',
          'What you saw, and how. We cannot open the file, so your description is our only evidence. The more specific it is, the better we can act.',
          'Your contact details, if you are willing to be contacted for clarification. Optional.',
        ],
      },
      {
        heading: 'Please do not send us the material',
        paragraphs: [
          'For the most serious category you should not send a copy to anyone: possessing and transmitting it may itself be an offence. Report it to us by identifier, and report it directly to the appropriate authority.',
        ],
      },
      {
        heading: 'What happens next',
        paragraphs: [
          'A report is recorded against the identifier with its reason, description and timestamp. Reports of child sexual abuse material and of malware are flagged for immediate human attention.',
          'A person then decides. Removal requires an operator credential and a deliberate action; nothing is deleted by the act of reporting, for the reasons set out in the acceptable use policy. If the share is destroyed, both the stored blob and its record go, irreversibly.',
        ],
      },
      {
        heading: 'What we can act on',
        table: {
          columns: ['Can we', 'Answer'],
          rows: [
            [
              'Delete a share from its identifier alone',
              'Yes, at any time, without decrypting anything.',
            ],
            ['Verify a report by inspecting the file', 'No. The key never reaches us.'],
            ['Search storage for similar material', 'No. There is nothing searchable to search.'],
            ['Tell you what was in a share, including one of your own', 'No.'],
          ],
        },
      },
      {
        heading: 'What we keep, and for how long',
        paragraphs: [
          'Reports outlive the shares they concern. A share lasts at most 24 hours, so a report about an expired link is often the only remaining record that it existed — which makes it the report most worth keeping, not the least. Reports are not deleted when the share is.',
        ],
      },
      {
        heading: 'Legal requests',
        paragraphs: lines(
          OPERATOR.contactEmail ? `Lawful requests should go to ${OPERATOR.contactEmail}.` : null,
          'Please read the law-enforcement table in the acceptable use policy first: it sets out precisely what we hold, which is very little, and what we cannot produce at all.',
        ),
      },
    ],
  },

  {
    slug: 'transparency-report',
    group: 'trust',
    navLabel: 'Transparency report',
    title: 'Transparency report',
    metaTitle: 'Transparency report — requests and removals',
    metaDescription:
      'What we have been asked for, what we removed, and what we were able to hand over. Published because a service that cannot be audited should at least be counted.',
    heading: 'Transparency report',
    intro:
      'A service that stores files it cannot read should say how often it is asked about them, and what it was able to provide. This page is the commitment to do that, published before there is anything to report.',
    sections: [
      {
        heading: 'Nothing to report yet',
        callout: {
          tone: 'info',
          text: 'There have been no shares, no reports and no legal requests, so every figure below is zero — and is published as zero rather than omitted, so that the first non-zero number is visible as a change rather than as a new page. This page is updated at least twice a year once sharing carries traffic.',
        },
      },
      {
        heading: 'Reporting period',
        paragraphs: [
          'This report will be updated at least twice a year once sharing is enabled, and will cover the period since the previous one.',
        ],
      },
      {
        heading: 'Abuse reports',
        table: {
          columns: ['Category', 'Received'],
          rows: [
            ['Child sexual abuse material', '0'],
            ['Malware', '0'],
            ['Phishing or fraud', '0'],
            ['Copyright', '0'],
            ['Personal data published without consent', '0'],
            ['Other', '0'],
          ],
        },
      },
      {
        heading: 'Action taken',
        table: {
          columns: ['Action', 'Count'],
          rows: [
            ['Links removed following a report', '0'],
            ['Links removed automatically pending review', '0'],
            ['Reports found not to warrant removal', '0'],
            ['Networks rate-limited or blocked', '0'],
          ],
        },
      },
      {
        heading: 'Legal requests',
        table: {
          columns: ['Request type', 'Received / complied'],
          rows: [
            ['Requests for stored file contents', '0 / 0'],
            ['Requests for user identity', '0 / 0'],
            ['Preservation orders', '0 / 0'],
            ['Takedown orders', '0 / 0'],
          ],
        },
      },
      {
        heading: 'What we are able to provide',
        paragraphs: [
          'This matters more than the counts, and does not change with the reporting period. In response to a valid legal request about a share link that has not yet expired, we can provide the encrypted blob, its size, and its timestamps.',
          'We cannot provide the address that created it, because we do not have one. The share record has no field for an address, and the rate limiter keeps only a salted hash it cannot reverse. This is worth stating precisely, because it is the question a request is most likely to ask.',
          'We cannot provide the contents, because we cannot decrypt them. The key exists only in the part of the link after the # symbol, which never reaches us. This is a property of how the feature is built, not a policy we could change under pressure — and it is why the 24-hour expiry cap matters: after that, there is nothing left to ask about.',
          'We have never received a request of any kind. If that ever ceases to be true, this sentence will be removed rather than quietly left in place.',
        ],
      },
    ],
  },

  {
    slug: 'threat-model',
    group: 'trust',
    navLabel: 'Threat model',
    title: 'Threat model',
    metaTitle: 'PrivatePDF threat model — what we can and cannot see',
    metaDescription:
      'Exactly who can see your document and who cannot: us, your ISP, our CDN, an ad network, a subpoena. Including what we do not defend against.',
    heading: 'Threat model',
    intro:
      'Being precise here is a feature, not a disclaimer. This page says what we defend against, what we do not, and where the limits are. If a statement stops being true, the statement changes — not the reader’s understanding of it.',
    sections: [
      {
        heading: 'Two guarantees, never merged into one',
        paragraphs: [
          'The product makes two promises and they are not equally strong. Any copy that blurs them is a bug, so they are stated separately everywhere they appear.',
        ],
        table: {
          columns: ['', 'Guarantee'],
          rows: [
            [
              'Editing — the default, and everything available today',
              'Zero upload. No route accepts document bytes. Verifiable in your browser’s network tab, and with the network disconnected entirely.',
            ],
            [
              'Sharing — not built yet',
              'When it exists it will be end-to-end encrypted: ciphertext is uploaded, the key is generated in your browser and travels in the URL fragment, which browsers never transmit. Until then, nothing you do here uploads anything.',
            ],
          ],
        },
        callout: {
          tone: 'info',
          text: 'The status bar in the editor reflects this literally. It reads “0 bytes uploaded” for the whole session, and is wired to real resource timings rather than to a hard-coded string.',
        },
      },
      {
        heading: 'What each adversary can and cannot do',
        table: {
          columns: ['Adversary', 'Posture'],
          rows: [
            [
              'Us, the operator',
              'Cannot see your file. No route accepts document bytes, and CI fails the build if one is added (scripts/check-routes.mjs).',
            ],
            [
              'Your network, ISP, or hostile Wi-Fi',
              'Cannot see your file. Nothing is transmitted while editing.',
            ],
            [
              'Cloudflare, our CDN and host',
              'Cannot see your file. They serve static assets and see your IP address, like any host.',
            ],
            [
              'A legal subpoena served on us',
              'Yields nothing about your documents, because nothing about them exists. We cannot hand over what we never received.',
            ],
            [
              'An ad network',
              'Cannot read your file — ads are sandboxed in a cross-origin iframe with no access to storage, worker memory or our JavaScript. Can see your IP, user agent and the page address, which reveals which tool you opened but never which document. No ads are running yet.',
            ],
            [
              'A malicious browser extension',
              'NOT DEFENDED. Extensions run inside the page with full access to it. No web application can defend against this, and any that claims to is wrong.',
            ],
            [
              'Malware on your device',
              'NOT DEFENDED. Out of scope for any web application.',
            ],
            [
              'Us, in a future release',
              'Partially defended, and this is the genuine weak point. See the transparency page — we do not paper over it.',
            ],
            [
              'Other websites, other tabs',
              'Defended by origin isolation and a strict Content-Security-Policy. Browser storage here is scoped to this origin.',
            ],
          ],
        },
      },
      {
        heading: 'Things we deliberately do not promise',
        bullets: [
          'Anonymity from our host. Cloudflare sees your IP address when you load the page, exactly as every website’s host does.',
          'Protection after the file leaves. If you export a result and send it somewhere else, that is outside our boundary.',
          'Defence against screen recording, or someone reading over your shoulder.',
          'That a document is safe to publish because a tool here reported it clean. Our hidden-content detector finds concealed text; it is not a guarantee that nothing was missed.',
        ],
      },
      {
        heading: 'How the guarantees are enforced',
        paragraphs: [
          'Not by policy, and not by our good intentions. Each of these fails the build when broken, which is the only form of promise worth making about software.',
        ],
        bullets: [
          'A route gate: no endpoint outside the (not yet existing) share module may accept a request body, and every other route is capped at 4 KB.',
          'A zone-isolation test: the editor is cross-origin isolated and its Content-Security-Policy contains no third-party origin.',
          'A network assertion: every tool is driven against a real file in a real browser, asserting zero requests carrying document data — including with the network cut off mid-operation.',
          'A redaction verification pass: exported bytes are re-read and searched for the text that was supposed to be removed. If any survives, the export fails rather than producing a file.',
          'A licence gate: no copyleft dependency can reach the shipped application.',
        ],
      },
    ],
  },

  {
    slug: 'transparency',
    group: 'trust',
    navLabel: 'Transparency',
    title: 'Transparency',
    metaTitle: 'Code transparency — the honest weak point of browser crypto',
    metaDescription:
      'We ship you JavaScript on every visit, so we could ship malicious JavaScript tomorrow. What we do about that, and what we have not built yet.',
    heading: 'Code transparency, and the honest weak point',
    intro:
      'Every product that does cryptography in a browser has the same unavoidable flaw, and most of them do not mention it. Ours is worth stating in one sentence before anything else.',
    sections: [
      {
        heading: 'The problem',
        callout: {
          tone: 'warn',
          text: 'We ship you the JavaScript on every page load, so we could ship malicious JavaScript tomorrow. No amount of client-side engineering removes that.',
        },
        paragraphs: [
          'What follows is what we actually do about it. We claim only what has shipped: the second list below is the work that does not exist yet, and it stays there until it does.',
        ],
      },
      {
        heading: 'In place today',
        bullets: [
          'A strict Content-Security-Policy in the editor: script-src \'self\' \'wasm-unsafe-eval\'. No inline script, no third-party origin, and no JavaScript eval. The one addition, \'wasm-unsafe-eval\', permits WebAssembly compilation and nothing else — without it PDFium and qpdf cannot start at all, so this is a requirement rather than a relaxation. A test fails the build if any cross-origin source ever appears in this policy.',
          'The editor is cross-origin isolated, with COOP same-origin and COEP require-corp. That is also what makes WebAssembly threads available.',
          'No third-party code runs in the document that touches your file. This is the narrow, defensible version of the claim — not “no third-party code ever”. Advertising is planned for the content pages, and pretending otherwise would be the same dishonesty we are selling against.',
          'Reproducible dependency resolution: a pinned toolchain, exact versions, and a frozen lockfile in CI.',
        ],
      },
      {
        heading: 'Not built yet',
        paragraphs: [
          'Listed because a transparency page that omits its own gaps is marketing.',
        ],
        bullets: [
          'A SIGNED build manifest. The manifest itself now exists: /build-manifest.json lists a SHA-256 of every file this deployment serves, and scripts/verify-manifest.mjs fetches a live site and checks every one of them. Two things are still missing, and they are the two that matter most. No signing key is configured, so it ships unsigned — which proves the files match the list, not who wrote the list. And it is not published to a public transparency log, without which a release is auditable but not provably immutable: we could serve you one manifest and somebody else another. The /api/manifest endpoint still returns a stub.',
          'A verifier extension that checks the page you loaded against that manifest on every visit.',
          'Public source, and a third-party audit. Both are prerequisites for any paid tier rather than something that follows one.',
          'A desktop build — the real answer for high-assurance users, since it does not change underneath you.',
        ],
      },
      {
        heading: 'Why in that order',
        paragraphs: [
          'The list is cheapest-first, and each item is worth something on its own. A strict policy does not stop a malicious release, but it does stop an accidental one, and it makes a deliberate one visible in a diff.',
          'The manifest and the transparency log are what turn “trust us” into “check us”. That is why they gate a paid tier rather than following one: charging for privacy before you can verify it would be the wrong way round.',
        ],
      },
    ],
  },

  {
    slug: 'security',
    group: 'trust',
    navLabel: 'Security',
    title: 'Security',
    metaTitle: 'Security policy — reporting a vulnerability',
    metaDescription:
      'How to report a security issue, what we treat as high severity, and what is out of scope.',
    heading: 'Security policy',
    intro:
      'If you have found something, we would rather hear it from you than from someone else.',
    sections: [
      {
        heading: 'Reporting',
        paragraphs: [
          'Please report security issues privately rather than in public. We aim to acknowledge within 72 hours.',
          'Include what you found, how to reproduce it, and what you think the impact is.',
        ],
        callout: {
          tone: 'info',
          text: 'If it involves a particular PDF, please describe its structure rather than sending the file. We would rather not receive your documents — that is rather the point of the product.',
        },
      },
      {
        heading: 'What we treat as high severity',
        paragraphs: [
          'Anything that breaks a guarantee on the threat model page.',
        ],
        bullets: [
          'Document bytes reaching a server during editing, by any path at all.',
          'Redacted content surviving in an exported file. Drawing a black box over live text is the classic catastrophic bug in PDF tooling and we treat any instance as critical — it is also what our own verification pass exists to make impossible.',
          'Loss of cross-origin isolation in the editor, or a third-party origin appearing in the editor’s Content-Security-Policy.',
          'The hidden-content detector missing a category of concealed text it claims to find.',
        ],
      },
      {
        heading: 'Out of scope',
        bullets: [
          'Malicious browser extensions and compromised devices, as set out in the threat model.',
          'Opening a very large file and exhausting your own browser’s memory. The pre-flight check refuses these deliberately; a crash instead of a refusal is a bug, a slow render is not.',
          'Reports that a tool did not find something it never claimed to look for. The pages say what each tool does and does not do.',
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ guides
  //
  // Formats people search for that this site deliberately does not convert.
  // Each says why, then points somewhere that does the job properly — a page
  // that only said "no" would be useless to the person who landed on it.

  {
    slug: 'word-to-pdf',
    group: 'guide',
    navLabel: 'Word to PDF',
    title: 'Word to PDF',
    metaTitle: 'Word to PDF — why we do not convert it, and what to use instead',
    metaDescription:
      'Why this site does not turn Word documents into PDFs, and the free ways to do it well: Word, Google Docs, Pages and LibreOffice.',
    heading: 'Word to PDF: use the program that wrote it',
    intro:
      'We do not convert Word documents to PDF, and we would rather tell you why than do it badly. The good news is that the best converter is almost certainly one you already have.',
    sections: [
      {
        heading: 'Why we do not offer it',
        paragraphs: [
          'A .docx file is not a page. It is text and instructions — this paragraph is a heading, keep these lines together, number these footnotes — and turning it into pages needs a complete layout engine and the exact fonts the document was written with. Word is that engine. A converter running in a browser tab has to reimplement it and guess at the fonts.',
          'The result of that guess is a PDF that looks nearly right: a line breaks one word earlier, a table spills onto the next page, a footnote moves. Those differences are easy to miss on screen and obvious once a document has been printed or signed. For something you are about to send, nearly right is the failure.',
        ],
      },
      {
        heading: 'What to use instead',
        table: {
          columns: ['Program', 'How'],
          rows: [
            ['Microsoft Word', 'File → Save As, and choose PDF as the type. On Windows, Options lets you keep the document’s structure tags for screen readers — leave that ticked.'],
            ['Google Docs', 'File → Download → PDF document.'],
            ['Apple Pages', 'File → Export To → PDF.'],
            ['LibreOffice', 'Free and works offline. File → Export as PDF. Tick “Tagged PDF” to keep headings and reading order for assistive technology.'],
          ],
        },
        callout: {
          tone: 'info',
          text: 'Each of these runs on your own machine, like everything here. None of them needs the document to be uploaded anywhere.',
        },
      },
      {
        heading: 'Once you have the PDF',
        paragraphs: [
          'Everything after the conversion is what this site is for: merging it with other files, compressing it, adding page numbers or a signature, and checking whether it will work with a screen reader before you publish it.',
        ],
      },
    ],
    related: ['check-pdf-accessibility', 'merge-pdf', 'compress-pdf', 'pdf-to-word'],
  },

  {
    slug: 'html-to-pdf',
    group: 'guide',
    navLabel: 'Web page to PDF',
    title: 'Web page to PDF',
    metaTitle: 'Web page to PDF — why we do not convert it, and what to use instead',
    metaDescription:
      'Why this site does not turn web pages or HTML into PDFs, and how to do it with the browser you already have.',
    heading: 'Web page to PDF: your browser already does it',
    intro:
      'We do not convert web pages or HTML files to PDF. Your browser is the best HTML renderer on your computer, and it can already save any page as a PDF.',
    sections: [
      {
        heading: 'Why we do not offer it',
        paragraphs: [
          'Rendering a web page means loading it: fetching its images, fonts and scripts from wherever they live. There are only two ways for a site like this one to do that. Either our server fetches the page for you — which means our server sees every address you convert, and often what is behind a login too — or the page’s own code runs inside our editor, which the editor’s security policy exists to forbid.',
          'Neither is compatible with the promise that your documents never leave your device. So rather than weaken that for one feature, we point you at the tool that already does it without any of those compromises.',
        ],
      },
      {
        heading: 'How to do it',
        bullets: [
          'Open the page, then print it (Ctrl+P, or ⌘P on a Mac).',
          'Choose “Save as PDF” as the printer. On Windows, “Microsoft Print to PDF” also works.',
          'Under more settings, turn on background graphics if colours or shading are missing, and turn off headers and footers to drop the date and address from each page.',
          'For an article, switch to the browser’s reader view first. The PDF comes out as the text, without the menus and advertising around it.',
        ],
      },
      {
        heading: 'For many pages at once',
        paragraphs: [
          'Chrome and Edge can print a page to PDF from the command line without opening a window, with the --headless and --print-to-pdf options. That suits a script that needs to archive a list of pages, and still happens entirely on your machine.',
        ],
      },
    ],
    related: ['merge-pdf', 'compress-pdf', 'crop-pdf', 'extract-text-from-pdf'],
  },

  {
    slug: 'pdf-to-pdfa',
    group: 'guide',
    navLabel: 'PDF to PDF/A',
    title: 'PDF to PDF/A',
    metaTitle: 'PDF to PDF/A — why we do not convert it, and what to use instead',
    metaDescription:
      'Why this site does not produce PDF/A archive files, why editing a PDF/A file here removes its conformance, and the free tools that convert and validate properly.',
    heading: 'PDF/A: convert it where it can be proved',
    intro:
      'PDF/A is the archival form of PDF, required by many courts, archives and public bodies. We do not produce it — and if you edit a PDF/A file here, the result is no longer PDF/A. This page explains both, and where to go instead.',
    sections: [
      {
        heading: 'Editing a PDF/A file here removes its conformance',
        callout: {
          tone: 'warn',
          text: 'Exporting from any tool on this site produces an ordinary PDF, even when the file you opened was PDF/A. The export rebuilds the document, and the parts that declare and support conformance — the archival metadata and the colour profile — are not carried over. Text this site adds also uses fonts that are not embedded, which PDF/A forbids. If a portal or archive requires PDF/A, do not upload a file you have exported from here without converting and validating it again.',
        },
      },
      {
        heading: 'Why we do not offer it',
        paragraphs: [
          'PDF/A is a promise that a file will display the same way decades from now, and the promise is only worth anything if it is checked. Every font must be embedded, colour must be defined by an embedded profile, the metadata must agree with itself, and anything that depends on the outside world — scripts, encryption, external links to content — must be absent.',
          'A converter that stamps “PDF/A” on a file without proving those things produces the worst possible result: a file that claims to be archival, is accepted today, and is rejected by the validator the archive runs later. We would rather send you to tools that validate than add one that only claims.',
        ],
      },
      {
        heading: 'What to use instead',
        table: {
          columns: ['Tool', 'What it does'],
          rows: [
            ['The program that made the document', 'The most reliable route. LibreOffice has an “Archive (PDF/A)” option in its PDF export, and Word’s PDF options include “PDF/A compliant”.'],
            ['Ghostscript', 'Free and offline. Its PDF writer can convert an existing PDF to PDF/A. Check the result, since not every input can be converted cleanly.'],
            ['veraPDF', 'The free, open-source validator backed by the PDF Association. It tells you whether a file really conforms and, if not, exactly which rule it breaks. Use it on whatever you produce.'],
          ],
        },
      },
    ],
    related: ['inspect-pdf', 'edit-pdf-metadata', 'check-pdf-accessibility', 'compress-pdf'],
  },
];

export const TRUST_PAGES: readonly DocPage[] = DOC_PAGES.filter((d) => d.group === 'trust');
export const GUIDE_PAGES: readonly DocPage[] = DOC_PAGES.filter((d) => d.group === 'guide');

export const DOC_SLUGS: readonly string[] = DOC_PAGES.map((d) => d.slug);

export const docBySlug = (slug: string): DocPage | undefined =>
  DOC_PAGES.find((d) => d.slug === slug);
